import React from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, Polyline } from 'react-leaflet';
import { PersonnelOps, statusMarkerColor, isOnline } from '../data/personnelOps';
import { useNavigate } from 'react-router-dom';
import { User, MapPin, Activity, Clock, Shield } from 'lucide-react';
import { Badge } from './Badge';
import { Button } from './Button';
interface PersonnelMapProps {
  personnel: PersonnelOps[];
  height?: number | string;
  showPathFor?: string; // ID of personnel to show path for
  center?: [number, number];
  zoom?: number;
}
export function PersonnelMap({
  personnel,
  height = 400,
  showPathFor,
  center = [-2.5489, 118.0149],
  zoom = 5
}: PersonnelMapProps) {
  const navigate = useNavigate();
  const pathPersonnel = showPathFor ? personnel.find(p => p.id === showPathFor) : null;
  const pathPoints = pathPersonnel?.movementHistory.map(m => [m.lat, m.lng] as [number, number]);
  return <div style={{
    height,
    width: '100%'
  }} className="rounded-md overflow-hidden border border-border/50 relative z-0" data-id="element-405">
      <MapContainer center={center} zoom={zoom} style={{
      height: '100%',
      width: '100%',
      background: '#0a0a0a'
    }} zoomControl={false} data-id="element-406">
        <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" attribution='&copy; <a href="https://carto.com/">CARTO</a>' data-id="element-407" />

        {/* Draw path if requested */}
        {pathPoints && pathPoints.length > 0 && <Polyline positions={pathPoints} color="var(--primary)" weight={3} opacity={0.6} dashArray="5, 10" data-id="element-408" />}

        {/* Draw points for path */}
        {pathPersonnel?.movementHistory.map((m, idx) => <CircleMarker key={`path-${idx}`} center={[m.lat, m.lng]} radius={4} fillColor="var(--primary)" color="var(--primary)" weight={1} fillOpacity={0.8} data-id="element-409">
            <Popup className="dens-popup" data-id="element-410">
              <div className="p-2 min-w-[200px]" data-id="element-411">
                <div className="text-xs font-mono text-primary mb-1" data-id="element-412">
                  {m.time}
                </div>
                <div className="font-bold text-sm mb-1" data-id="element-413">{m.label}</div>
                <div className="text-xs text-muted-foreground" data-id="element-414">{m.note}</div>
              </div>
            </Popup>
          </CircleMarker>)}

        {/* Draw current positions */}
        {personnel.map(p => {
        const pos = p.currentPosition;
        const color = statusMarkerColor(p.status);
        const isTarget = p.id === showPathFor;
        return <CircleMarker key={p.id} center={[pos.lat, pos.lng]} radius={isTarget ? 8 : 6} fillColor={color} color={color} weight={isTarget ? 3 : 1} fillOpacity={0.8} data-id="element-415">
              <Popup className="dens-popup" data-id="element-416">
                <div className="p-3 min-w-[240px] bg-background/95 border border-border/50 rounded-md shadow-xl backdrop-blur-sm" data-id="element-417">
                  <div className="flex items-start gap-3 mb-3" data-id="element-418">
                    <img src={p.photo} alt={p.name} className="w-10 h-10 rounded-full border border-border/50" data-id="element-419" />
                    <div data-id="element-420">
                      <div className="font-bold text-sm leading-tight" data-id="element-421">
                        {p.name}
                      </div>
                      <div className="text-[10px] font-mono text-muted-foreground" data-id="element-422">
                        {p.id} • {p.role}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 mb-3" data-id="element-423">
                    <div className="flex items-center justify-between text-xs" data-id="element-424">
                      <span className="text-muted-foreground flex items-center gap-1" data-id="element-425">
                        <Activity className="w-3 h-3" data-id="element-426" /> Status
                      </span>
                      <span style={{
                    color
                  }} data-id="element-427">
                        {p.status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs" data-id="element-428">
                      <span className="text-muted-foreground flex items-center gap-1" data-id="element-429">
                        <MapPin className="w-3 h-3" data-id="element-430" /> Lokasi
                      </span>
                      <span className="truncate max-w-[120px] text-right" title={pos.kecamatan} data-id="element-431">
                        {pos.kecamatan}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs" data-id="element-432">
                      <span className="text-muted-foreground flex items-center gap-1" data-id="element-433">
                        <Clock className="w-3 h-3" data-id="element-434" /> Terakhir
                      </span>
                      <span data-id="element-435">{p.lastActivity}</span>
                    </div>
                  </div>

                  <Button size="sm" className="w-full h-7 text-[10px] font-mono" onClick={e => {
                e.stopPropagation();
                navigate(`/personnel/${p.id}`);
              }} data-id="element-436">
                    Lihat Detail
                  </Button>
                </div>
              </Popup>
            </CircleMarker>;
      })}
      </MapContainer>
    </div>;
}